// 参数字段联动显隐 (前端写死版, 2026-10-20).
// 设计见 doc/参数模板字段联动显隐-设计方案-20261020.md; 规则对照 xcs-cms 各设备参数页 (JS001/JS002/DQ003/CF001/
// materialParamModal) 的 showWhen 与 switch-group, 按字段 key 写、对所有模板通用.
//
// 口径:
//   - 只影响参数编辑/详情页的显示; 被隐藏字段的已有值原样保留、照常提交 (不改存储语义, 不影响下游).
//   - 门控字段不在当前模板里 → 规则不生效, 字段照常显示 (宁可多显示, 不误藏).
//   - 门控字段在模板里但自己被隐藏 → 被控字段一并隐藏 (链式: cuttingDrop 关 → sinkingMethod 藏 → 下沉值藏).
//   - 开关类门控未填值按"关"算; 枚举类门控未填值用 dflt (与 xcs-cms 默认值一致), 无 dflt 则按"空"算.
// 以后规则挪到后端 (模板/字典 when 属性) 时, 只换 FIELD_VISIBILITY 的来源, visibleFieldKeys 不动.

export type VisCond =
    /** 开关类: 值为真 (true / 1 / '1' / 'true') 才显示 */
    | { f: string; on: true; dflt?: boolean }
    /** 枚举类: 值属于 in 才显示 */
    | { f: string; in: (string | number)[]; dflt?: string | number }
    /** 枚举类: 值属于 nin 时隐藏, 其余 (含未填) 显示 */
    | { f: string; nin: (string | number)[] };

/** 被控字段 → 显示条件 (多条 = 全部满足) */
export type VisRules = Record<string, VisCond[]>;

const on = (f: string, dflt?: boolean): VisCond => ({ f, on: true, dflt });

function group(keys: string[], conds: VisCond[]): VisRules {
    const out: VisRules = {};
    for (const k of keys) out[k] = conds;
    return out;
}

export const FIELD_VISIBILITY: VisRules = {
    // 割缝补偿
    kerfDistance: [on('enableKerf')],
    // 焦点下沉: 开关 → 下沉方式 → 一次性 / 逐步 两套值
    sinkingMethod: [on('cuttingDrop')],
    cuttingDropValue: [
        on('cuttingDrop'),
        { f: 'sinkingMethod', in: ['one'], dflt: 'one' },
    ],
    ...group(
        ['firstCuttingDropValue', 'descentIntervalDescent', 'descentPerStep'],
        [on('cuttingDrop'), { f: 'sinkingMethod', in: ['step'], dflt: 'one' }],
    ),
    // 断点: 开关 → 断点方式 → 按次数 / 按距离
    ...group(
        [
            'breakPointMode',
            'breakPointGenMode',
            'breakPointSize',
            'breakPointPower',
        ],
        [on('enableBreakPoint')],
    ),
    breakPointCount: [
        on('enableBreakPoint'),
        { f: 'breakPointMode', in: ['count'], dflt: 'count' },
    ],
    breakPointDistance: [
        on('enableBreakPoint'),
        { f: 'breakPointMode', in: ['distance'], dflt: 'count' },
    ],
    // 浮雕清扫
    ...group(
        [
            'cleanUpLayers',
            'cleanUpPower',
            'cleanUpSpeed',
            'cleanUpRepeat',
            'cleanUpDensity',
            'cleanUpProcessAngle',
            'cleanUpMopaFrequency',
            'cleanUpPulseWidth',
        ],
        [on('reliefCleanUp')],
    ),
    // 加强 / XY 细分
    ...group(
        ['strengthDirection', 'strengthenMultiple'],
        [on('strengthenEnable')],
    ),
    xySubdivide: [on('xySubdivideEnable')],
    // Z 轴移动 (JS001 用 vLayers/vDecline, 其余设备用 zLayers/zDecline)
    ...group(['vLayers', 'vDecline', 'zLayers', 'zDecline'], [on('zAxisMove')]),
    // 位图雕刻方式: 打点 (dot) 不用速度/密度, 普通 (normal) 不用打点时间/DPI;
    // 内雕方式: 线填充 (line_fill) 不用打点时间/细分/打印纹理, 点云 (dot_cloud) 不用速度.
    // bitmapScanMode 不跟雕刻方式: xcs-cms JS001 只在填充雕刻里藏, 位图雕刻打点仍显示 (X1 位图模板实测误藏).
    density: [{ f: 'bitmapEngraveMode', nin: ['dot'] }],
    dpi: [{ f: 'bitmapEngraveMode', nin: ['normal'] }],
    speed: [
        { f: 'bitmapEngraveMode', nin: ['dot'] },
        { f: 'innerProcessMode', nin: ['dot_cloud'] },
    ],
    dotDuration: [
        { f: 'bitmapEngraveMode', nin: ['normal'] },
        { f: 'innerProcessMode', nin: ['line_fill'] },
    ],
    ...group(
        ['subdivide', 'isPrintTexture'],
        [{ f: 'innerProcessMode', nin: ['line_fill'] }],
    ),
    // 灰度位图才有功率范围 (耗材参数管理落 minPower, JS001 落 powerMinMaxRange)
    ...group(
        ['minPower', 'powerMinMaxRange'],
        [{ f: 'bitmapMode', in: ['grayscale'] }],
    ),
    // 离焦
    defocus_distance: [on('defocus')],
    // 摆动
    ...group(
        ['wobbleDiameter', 'wobbleSpacing', 'wobbleSpeed'],
        [on('wobbleEnable')],
    ),
    // DQ003 刀切: 填充方式 = 3 才有线填充参数
    ...group(
        [
            'lineFillMode',
            'lineRangle',
            'lineWidth',
            'bleedLine',
            'connectFactor',
        ],
        [{ f: 'fillMode', in: [3] }],
    ),
    // DQ003 画笔填充: 加粗描边才有线偏移
    lineOffset: [on('enableBoldStroke')],
    // DQ003: 分层压力
    ...group(['cut_times', 'pressure_layers'], [on('pressureMode')]),
    // DQ003 烫金: 无色箔才有温度
    temperature_c: [{ f: 'foilColor', in: ['colorless'] }],
    // DQ003 画笔: 防积墨
    ...group(
        ['brushAntiInkLevel', 'pen_Z', 'pen_length', 'pen_density'],
        [on('brushAntiInkEnabled')],
    ),
    // CF001 刺绣: 拉力补偿 (默认开启)
    pullCompensation: [on('pullCompensationEnabled', true)],
};

function isOn(v: unknown): boolean {
    return v === true || v === 1 || v === '1' || v === 'true';
}

function isEmpty(v: unknown): boolean {
    return v === undefined || v === null || v === '';
}

function condHolds(c: VisCond, v: unknown): boolean {
    if ('on' in c) return isEmpty(v) ? !!c.dflt : isOn(v);
    if ('in' in c) {
        const cur = isEmpty(v) ? c.dflt : v;
        if (cur === undefined) return false;
        return c.in.some((x) => String(x) === String(cur));
    }
    if (isEmpty(v)) return true;
    return !c.nin.some((x) => String(x) === String(v));
}

/**
 * 计算当前参数值下可见的字段 key 集合.
 * @param keys   当前模板 (表单) 里的字段 key, 门控字段不在其中时规则不生效
 * @param params 当前参数值
 */
export function visibleFieldKeys(
    keys: string[],
    params: Record<string, unknown>,
    rules: VisRules = FIELD_VISIBILITY,
): Set<string> {
    const inForm = new Set(keys);
    const memo = new Map<string, boolean>();
    const visiting = new Set<string>();
    const visible = (k: string): boolean => {
        const hit = memo.get(k);
        if (hit !== undefined) return hit;
        // 规则成环时按可见处理, 不让字段凭空消失
        if (visiting.has(k)) return true;
        visiting.add(k);
        let ok = true;
        for (const c of rules[k] || []) {
            if (!inForm.has(c.f)) continue;
            if (!visible(c.f) || !condHolds(c, params[c.f])) {
                ok = false;
                break;
            }
        }
        visiting.delete(k);
        memo.set(k, ok);
        return ok;
    };
    return new Set(keys.filter(visible));
}

/** 字段的门控字段 key 列表 (模板编辑页提示用); 无规则返回空数组 */
export function gateFieldsOf(
    k: string,
    rules: VisRules = FIELD_VISIBILITY,
): string[] {
    return Array.from(new Set((rules[k] || []).map((c) => c.f)));
}
